import { ArrowLeftOutlined, BarChartOutlined, CalendarOutlined, CheckCircleOutlined, ClockCircleOutlined, DownOutlined, ExclamationCircleOutlined, EyeOutlined, PlayCircleOutlined, UpOutlined, UserOutlined, WarningFilled } from "@ant-design/icons";
import { Button, Card, Col, Divider, Empty, Input, message, Row, Select, Space, Tabs, Typography } from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import { AutoSizer, CellMeasurer, CellMeasurerCache, List } from "react-virtualized";
import { Loading } from "../../../components/Loading";
import { TreeSelectLocationUser } from "../../../components/TreeSelectLocation";
import { UserPicker } from "../../../components/UserPicker";
import { useAuth } from "../../../hooks/auth";
import Api from "../../../services/api";
import NewPowerBI from "../../Dashboard/newBi";
import { useTranslation } from "react-i18next";
import moment from "moment";

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { Search } = Input;
const { Option } = Select;

const StatusCards = ({ statusCount, t }) => {
    const quickActions = [
        {
            key: "late",
            title: "Tarefas Atrasadas",
            description: `Quantidade de tarefas com prazo vencido: ${statusCount.Late}`,
            icon: ExclamationCircleOutlined,
            color: "#f87171",
            count: statusCount.Late,
        },
        {
            key: "open",
            title: "Tarefas em Andamento",
            description: `Quantidade de tarefas em execução ou pendentes: ${statusCount.Pending}`,
            icon: PlayCircleOutlined,
            color: "#60a5fa",
            count: statusCount.Pending,
        },
        {
            key: "approval",
            title: "Aguardando Aprovação",
            description: `Quantidade de tarefas aguardando aprovação: ${statusCount.Approval}`,
            icon: ClockCircleOutlined,
            color: "#60a5fa",
            count: statusCount.Approval,
        },
        {
            key: "done",
            title: "Tarefas Concluídas",
            description: `Quantidade de tarefas finalizadas: ${statusCount.Done}`,
            icon: CheckCircleOutlined,
            color: "#34d399",
            count: statusCount.Done,
        },
    ];

    return (
        <Row gutter={[8, 8]} wrap align="stretch" style={{ marginBottom: 16 }}>
            {quickActions.map((action) => {
                const Icon = action.icon;
                return (
                    <Col
                        xs={24}
                        sm={12}
                        md={8}
                        lg={8}
                        xl={6}
                        key={action.key}
                        style={{ height: "100%" }}
                    >
                        <Card
                            style={{
                                height: "100%",
                                minHeight: 100,
                                display: "flex",
                                alignItems: "center",
                            }}
                        >
                            <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
                                <Icon
                                    style={{
                                        fontSize: 28,
                                        color: action.color,
                                        marginRight: 12,
                                    }}
                                />
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <Text
                                        style={{
                                            marginBottom: 4,
                                            whiteSpace: 'normal',
                                            wordBreak: 'break-word',
                                        }}
                                    >
                                        {action.title}
                                    </Text>
                                    <Title
                                        level={4}
                                        style={{
                                            marginTop: 0,
                                            marginBottom: 4,
                                            whiteSpace: 'normal',
                                            wordBreak: 'break-word',
                                        }}
                                    >
                                        {action.count}
                                    </Title>
                                </div>
                            </div>
                        </Card>
                    </Col>
                );
            })}
        </Row>
    );
};

const FormDetails = ({ formId, onClose }) => {
    const { user } = useAuth();
    const { t } = useTranslation();

    const [respostas, setRespostas] = useState([]);
    const [loading, setLoading] = useState(false);
    const [expandedId, setExpandedId] = useState(null);
    const [searchText, setSearchText] = useState("");
    const [createdByFilter, setCreatedByFilter] = useState(null);
    const [statusFilter, setStatusFilter] = useState([]);
    const [locationFilter, setLocationFilter] = useState(null);
    const [filterScope, setFilterScope] = useState(1);

    useEffect(() => {
        async function fetchTasks() {
            setLoading(true);
            try {
                const res = await Api.post(`DashboardCustomFlowy/getTasksByForm/${formId.id}/${filterScope}`);
                const { open = [], late = [], done = [], approval = [] } = res.data?.response || {};
                setRespostas([...open, ...late, ...done, ...approval]);
            } catch (err) {
                console.error(err);
                message.error("Falha ao carregar as tarefas.");
            } finally {
                setLoading(false);
            }
        }
        if (formId?.id) fetchTasks();
    }, [formId, filterScope]);

    const statusCount = useMemo(
        () =>
            respostas.reduce((acc, r) => {
                acc[r.status] = (acc[r.status] || 0) + 1;
                return acc;
            }, { Pending: 0, Late: 0, Done: 0, Approval: 0 }),
        [respostas]
    );

    const filteredRespostas = useMemo(() => {
        return respostas.filter((r) => {
            if (searchText) {
                const txt = searchText.toLowerCase();
                if (
                    !r.friendlyId?.toString().includes(searchText) &&
                    !r.description?.toLowerCase().includes(txt)
                )
                    return false;
            }

            if (createdByFilter && r.insertUser != createdByFilter) return false;
            if (statusFilter.length && !statusFilter.includes(r.status)) return false;
            if (locationFilter && r.locationId != locationFilter) return false;
            return true;
        });
    }, [respostas, searchText, createdByFilter, statusFilter, locationFilter]);

    const formatDate = (data) => (data ? moment(data).format('YYYY-MMM-DD HH:mm') : "—");

    const cache = useRef(
        new CellMeasurerCache({
            fixedWidth: true,
            defaultHeight: 120,
            minHeight: 60,
        })
    );

    const listRef = useRef();

    useEffect(() => {
        cache.current.clearAll();
        listRef.current?.recomputeRowHeights();
        listRef.current?.forceUpdateGrid();
    }, [expandedId, filteredRespostas]);

    const rowRenderer = ({ key, index, parent, style }) => {
        const resp = filteredRespostas[index];
        const workflowItems = resp.correlateds || [];
        const isExpanded = expandedId === resp.id;
        const statusColor = {
            Pending: "rgb(199 200 202)",
            Open: "#4085c6",
            Late: "#ff4d4f",
            Done: "#34d399",
            Approval: "#60a5fa",
        }[resp.status] || "#888";

        return (
            <CellMeasurer
                key={key}
                cache={cache.current}
                columnIndex={0}
                parent={parent}
                rowIndex={index}
            >
                <div style={{ ...style, paddingBottom: 12 }}>
                    <Card bodyStyle={{ padding: 0 }} style={{ overflow: "hidden", marginBottom: 0 }}>
                        <div style={{ background: "#fff", padding: 12, borderLeft: `4px solid ${statusColor}` }}>
                            <Row justify="space-between" align="middle">
                                <Col>
                                    <Space size="middle">
                                        <div
                                            style={{
                                                backgroundColor: statusColor,
                                                color: "#fff",
                                                fontWeight: 500,
                                                borderRadius: 8,
                                                padding: "2px 8px",
                                                fontSize: 12,
                                                display: "inline-block",
                                            }}
                                        >
                                            {t(resp.status)}
                                        </div>
                                        {resp.approvalPending && resp.status == "Approval" && <div style={{ backgroundColor: "#ffaa00", color: '#fff', fontWeight: 500, borderRadius: 8, padding: '2px 8px', fontSize: 12, display: 'inline-block' }}>
                                            <WarningFilled /> {t("Home.WaitingApproval")}
                                        </div>}
                                        <Text strong>
                                            <small>{t('Code')}</small>: {resp.friendlyId ?? resp.id}
                                        </Text>
                                        {workflowItems.length > 0 && (
                                            <Button
                                                type="text"
                                                size="small"
                                                style={{ marginRight: 5 }}
                                                icon={isExpanded ? <UpOutlined /> : <DownOutlined />}
                                                onClick={() => setExpandedId(isExpanded ? null : resp.id)}
                                            >
                                                Workflow
                                            </Button>
                                        )}
                                        <Button
                                            type="text"
                                            size="small"
                                            icon={<EyeOutlined />}
                                            onClick={() => {
                                                if (resp.status === "Done") {
                                                    window.open(`/formbuilder/view-answer/${resp.id}`, "_blank", "noopener,noreferrer");
                                                } else {
                                                    window.open(`/formbuilder/apply/${formId.id}/${resp.id}`, "_blank", "noopener,noreferrer");
                                                }
                                            }}
                                        >
                                            Visualizar
                                        </Button>
                                    </Space>
                                </Col>
                            </Row>
                            <Divider style={{ margin: '10px 0px 0px 0px' }} />
                            <Row gutter={[8, 8]} style={{ marginTop: 12 }}>
                                <Col span={6}>
                                    <Text type="secondary" style={{ fontSize: 12 }}>{t('Description')}</Text>
                                    <br />
                                    <Text strong>{resp.description || resp.name}</Text>
                                </Col>
                                <Col span={2}>
                                    <Text type="secondary" style={{ fontSize: 12 }}>{t('Version')}</Text>
                                    <br />
                                    <Text strong>{resp.version + 1}</Text>
                                </Col>
                                <Col span={4}>
                                    <Text type="secondary" style={{ fontSize: 12 }}>{t('Location')}</Text>
                                    <br />
                                    <Text strong>{resp.locationName}</Text>
                                </Col>
                                <Col span={3}>
                                    <Text type="secondary" style={{ fontSize: 12 }}>{t('CreatedBy')}</Text>
                                    <br />
                                    <Text strong>{resp.insertUserName}</Text>
                                </Col>
                                <Col span={3}>
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        <CalendarOutlined style={{ marginRight: 4 }} />
                                        {t('CreatedAt')}
                                    </Text>
                                    <br />
                                    <Text strong>{formatDate(resp.insertDate)}</Text>
                                </Col>
                                <Col span={3}>
                                    <Text type="secondary" style={{ fontSize: 12 }}>Responsável</Text>
                                    <br />
                                    <Text strong>{resp.userName}</Text>
                                </Col>
                                <Col span={3}>
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        <CalendarOutlined style={{ marginRight: 4 }} />
                                        {t('DueDate')}
                                    </Text>
                                    <br />
                                    <Text strong>{formatDate(resp.scheduleDate)}</Text>
                                </Col>
                            </Row>
                        </div>

                        {isExpanded && (
                            <>
                                <Divider style={{ margin: 0 }} />
                                <div style={{ padding: 16, background: "#fff" }}>
                                    <Title level={5}>{t('Menu.Workflow')}</Title>
                                    {workflowItems.map((item, idx) => {
                                        const itemColor = {
                                            Pending: "rgb(199 200 202)",
                                            Open: "#4085c6",
                                            Late: "#ff4d4f",
                                            Done: "#34d399",
                                            Approval: "#60a5fa",
                                        }[item.status] || "#888";
                                        return <>
                                            <div style={{ backgroundColor: itemColor, color: "#fff" }}>
                                                {item.formSequence}
                                            </div>
                                            <Card
                                                key={idx}
                                                bodyStyle={{ padding: 0 }}
                                                style={{
                                                    background: "#f9fafb",
                                                    padding: 12,
                                                    borderLeft: `4px solid ${itemColor}`,
                                                    marginBottom: 12,
                                                }}>
                                                <Row justify="space-between" align="middle">
                                                    <Col>
                                                        <Space size="middle">
                                                            <div
                                                                style={{
                                                                    backgroundColor: statusColor,
                                                                    color: "#fff",
                                                                    fontWeight: 500,
                                                                    borderRadius: 8,
                                                                    padding: "2px 8px",
                                                                    fontSize: 12,
                                                                    display: "inline-block",
                                                                    marginRight: 8,
                                                                }}
                                                            >
                                                                {t(resp.status)}
                                                            </div>
                                                            <Text strong style={{ fontSize: 16 }}>
                                                                <small>{t('Code')}</small>: {item.friendlyId ?? item.id}
                                                            </Text>
                                                            <Button
                                                                type="text"
                                                                size="small"
                                                                icon={<EyeOutlined />}
                                                                onClick={() => {
                                                                    if (resp.status === "Done") {
                                                                        window.open(`/formbuilder/view-answer/${item.id}`, "_blank", "noopener,noreferrer");
                                                                    } else {
                                                                        window.open(`/formbuilder/apply/${item.formId}/${item.id}`, "_blank", "noopener,noreferrer");
                                                                    }
                                                                }}
                                                            >
                                                                {t('User.View')}
                                                            </Button>
                                                        </Space>
                                                    </Col>
                                                </Row>
                                                <Divider style={{ margin: '10px 0px 0px 0px' }} />
                                                <Row gutter={[8, 8]}>
                                                    <Col span={3}>
                                                        <Text type="secondary" style={{ fontSize: 12 }}>{t('ConfigPanel.Form.FormName')}</Text>
                                                        <br />
                                                        <Text strong>{item.formName}</Text>
                                                    </Col>
                                                    <Col span={4}>
                                                        <Text type="secondary" style={{ fontSize: 12 }}>{t('Description')}</Text>
                                                        <br />
                                                        <Text strong>{item.description || item.name}</Text>
                                                    </Col>
                                                    <Col span={2}>
                                                        <Text type="secondary" style={{ fontSize: 12 }}>{t('Version')}</Text>
                                                        <br />
                                                        <Text strong>{item.version + 1}</Text>
                                                    </Col>
                                                    <Col span={3}>
                                                        <Text type="secondary" style={{ fontSize: 12 }}>{t('Location')}</Text>
                                                        <br />
                                                        <Text strong>{item.locationName}</Text>
                                                    </Col>
                                                    <Col span={3}>
                                                        <Text type="secondary" style={{ fontSize: 12 }}>{t('CreatedBy')}</Text>
                                                        <br />
                                                        <Text strong>{item.insertUserName}</Text>
                                                    </Col>
                                                    <Col span={3}>
                                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                                            <CalendarOutlined style={{ marginRight: 4 }} />
                                                            {t('CreatedAt')}
                                                        </Text>
                                                        <br />
                                                        <Text strong>{formatDate(item.insertDate)}</Text>
                                                    </Col>
                                                    <Col span={3}>
                                                        <Text type="secondary" style={{ fontSize: 12 }}>{t('AssignedTo')}</Text>
                                                        <br />
                                                        <Text strong>{item?.userName}</Text>
                                                    </Col>
                                                    <Col span={3}>
                                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                                            <CalendarOutlined style={{ marginRight: 4 }} />
                                                            {t('DueDate')}
                                                        </Text>
                                                        <br />
                                                        <Text strong>{formatDate(item.scheduleDate)}</Text>
                                                    </Col>
                                                </Row >
                                            </Card >
                                        </>
                                    })}
                                </div>
                            </>
                        )
                        }
                    </Card >
                </div >
            </CellMeasurer >
        );
    };

    return (
        <div>
            <div style={{ display: "flex", alignItems: "center", marginBottom: 16 }}>
                <Button
                    type="text"
                    size="small"
                    icon={<ArrowLeftOutlined />}
                    onClick={onClose}
                    style={{ marginRight: 8 }}
                />
                <Title level={3} style={{ margin: 0 }}>
                    {formId?.title}
                </Title>
            </div>

            <StatusCards statusCount={statusCount} />

            {loading ? (
                <Loading />
            ) : (
                <><Tabs defaultActiveKey="1">
                    <TabPane tab={<><UserOutlined /> Respostas</>} key="1">
                        <Row gutter={[8, 8]} style={{ marginBottom: 16 }}>
                            <Col span={4}>
                                <Search
                                    placeholder="Filtrar código / descrição"
                                    allowClear
                                    onSearch={setSearchText}
                                    style={{ width: "100%" }}
                                />
                            </Col>
                            <Col span={4}>
                                <UserPicker
                                    placeholder="Criado por"
                                    onChange={setCreatedByFilter}
                                    value={createdByFilter}
                                    style={{ width: "100%" }}
                                />
                            </Col>
                            <Col span={4}>
                                <Select
                                    mode="multiple"
                                    allowClear
                                    placeholder="Status"
                                    style={{ width: "100%" }}
                                    value={statusFilter}
                                    onChange={setStatusFilter}
                                >
                                    <Option value="Pending">Pending</Option>
                                    <Option value="Late">Late</Option>
                                    <Option value="Done">Done</Option>
                                    <Option value="Approval">Approval</Option>
                                </Select>
                            </Col>
                            <Col span={4}>
                                <TreeSelectLocationUser
                                    placeholder="Local"
                                    onChange={setLocationFilter}
                                    value={locationFilter}
                                    style={{ width: "100%" }}
                                />
                            </Col>
                            <Col span={4}>
                                <Select
                                    style={{ width: '100%' }}
                                    value={filterScope}
                                    onChange={(e) => setFilterScope(e)}
                                    disabled={user?.id != formId.insertUser}
                                >
                                    <Option value={1}>Minhas tarefas</Option>
                                    {!user?.showOnlyMyTasks && <Option value={2}>Tarefas da minha localização</Option>}
                                </Select>
                            </Col>
                        </Row>

                        {filteredRespostas.length === 0 ? (
                            <Empty description="Nenhuma resposta encontrada" style={{ margin: '40px auto' }} />
                        ) : (
                            <div style={{ height: "calc(80vh)" }}>
                                <AutoSizer>
                                    {({ height, width }) => (
                                        <List
                                            ref={listRef}
                                            width={width}
                                            height={height}
                                            rowCount={filteredRespostas.length}
                                            rowHeight={cache.current.rowHeight}
                                            deferredMeasurementCache={cache.current}
                                            rowRenderer={rowRenderer}
                                            overscanRowCount={7}
                                            scrollToAlignment="auto"
                                        />
                                    )}
                                </AutoSizer>
                            </div>
                        )}
                    </TabPane>

                    <TabPane tab={<><BarChartOutlined /> Dashboard Standard</>} key="2">
                        <div style={{ margin: "-15px -16px 0px -16px" }}>
                            <NewPowerBI formId={formId?.baseId} userId={user?.id == formId.insertUser ? null : user?.id} />
                        </div>
                    </TabPane>
                </Tabs>
                </>
            )
            }
        </div >
    );
};

export default FormDetails;
